import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { CreditCard, DollarSign, Plus, Check, X, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';

export default function AdminPayments() {
  const [tab, setTab] = useState('ledger');
  const [payments, setPayments] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRateForm, setShowRateForm] = useState(false);
  const [rateForm, setRateForm] = useState({ family: '', variant: '', state: '', client_price: '', lawyer_payout: '', agent_payout: '', priority: 0 });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDetail, setPaymentDetail] = useState(null);
  const [paymentEvents, setPaymentEvents] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { loadData(); }, [tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tab === 'ledger') {
        const { data } = await api.get('/payments/ledger');
        setPayments(data.payments);
      } else if (tab === 'payouts') {
        const { data } = await api.get('/payments/payouts');
        setPayouts(data);
      } else if (tab === 'rates') {
        const { data } = await api.get('/payments/rates');
        setRates(data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreateRate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payments/rates', {
        ...rateForm,
        client_price: parseFloat(rateForm.client_price),
        lawyer_payout: parseFloat(rateForm.lawyer_payout),
        agent_payout: parseFloat(rateForm.agent_payout || 0),
        priority: parseInt(rateForm.priority || 0),
      });
      setShowRateForm(false);
      setRateForm({ family: '', variant: '', state: '', client_price: '', lawyer_payout: '', agent_payout: '', priority: 0 });
      await loadData();
    } catch (err) { console.error(err); }
  };

  const handleApprovePayout = async (payoutId) => {
    try {
      await api.post(`/payments/payouts/${payoutId}/approve`);
      await loadData();
    } catch (err) { console.error(err); }
  };

  const openPaymentDetail = async (paymentId) => {
    setSelectedPayment(paymentId);
    setPaymentDetail(null);
    setPaymentEvents([]);
    setDetailLoading(true);
    try {
      const [d, e] = await Promise.all([
        api.get(`/payments/${paymentId}`),
        api.get(`/payments/${paymentId}/events`),
      ]);
      setPaymentDetail(d.data);
      setPaymentEvents(e.data.events || []);
    } catch (err) { console.error(err); }
    finally { setDetailLoading(false); }
  };

  const STATUS_COLORS = {
    created: 'bg-gray-100 text-gray-700', pending: 'bg-yellow-100 text-yellow-700',
    captured: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700',
    approved: 'bg-blue-100 text-blue-700', paid: 'bg-green-100 text-green-700',
  };

  return (
    <div data-testid="admin-payments">
      <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Payments & Payouts</h1>
      <p className="text-sm text-gray-500 mb-6">Financial operations center</p>

      <div className="flex border-b border-gray-200 mb-6">
        {[{ id: 'ledger', label: 'Payment Ledger' }, { id: 'payouts', label: 'Payouts' }, { id: 'rates', label: 'Rate Rules' }].map(t => (
          <button key={t.id} data-testid={`payments-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.id ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'ledger' && (
        <div className="border border-gray-200">
          <table className="w-full" data-testid="payment-ledger-table">
            <thead><tr className="border-b border-gray-200 bg-surface">
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Order ID</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Amount</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Status</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Date</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">Loading...</td></tr> :
               payments.length === 0 ? <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">No payments yet</td></tr> :
               payments.map(p => (
                <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    data-testid={`payment-row-${p.id}`} onClick={() => openPaymentDetail(p.id)}>
                  <td className="px-4 py-3 font-mono text-xs">{p.razorpay_order_id?.substring(0, 20) || '—'}</td>
                  <td className="px-4 py-3 text-sm font-medium">₹{p.amount}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs font-mono uppercase rounded-sm ${STATUS_COLORS[p.status] || 'bg-gray-100'}`}>{p.status}</span></td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-400">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'payouts' && (
        <div className="border border-gray-200">
          <table className="w-full" data-testid="payout-ledger-table">
            <thead><tr className="border-b border-gray-200 bg-surface">
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Role</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Amount</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Status</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Actions</th>
            </tr></thead>
            <tbody>
              {payouts.length === 0 ? <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">No payouts yet</td></tr> :
               payouts.map(p => (
                <tr key={p.id} className="border-b border-gray-100">
                  <td className="px-4 py-3 text-sm font-medium capitalize">{p.user_role}</td>
                  <td className="px-4 py-3 text-sm">₹{p.amount}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs font-mono uppercase rounded-sm ${STATUS_COLORS[p.status] || 'bg-gray-100'}`}>{p.status}</span></td>
                  <td className="px-4 py-3">
                    {p.status === 'pending' && (
                      <button onClick={() => handleApprovePayout(p.id)} data-testid={`approve-payout-${p.id}`}
                        className="text-xs px-3 py-1 bg-primary text-white rounded-sm hover:bg-primary-hover transition flex items-center gap-1">
                        <Check className="w-3 h-3" /> Approve
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'rates' && (
        <div>
          <div className="flex justify-end mb-4">
            <button data-testid="add-rate-btn" onClick={() => setShowRateForm(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-black text-white text-sm rounded-sm hover:bg-gray-800 transition">
              <Plus className="w-4 h-4" /> Add Rate Rule
            </button>
          </div>
          <div className="border border-gray-200">
            <table className="w-full" data-testid="rate-rules-table">
              <thead><tr className="border-b border-gray-200 bg-surface">
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Family</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Variant</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">State</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Client Price</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Lawyer</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Agent</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Priority</th>
              </tr></thead>
              <tbody>
                {rates.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">No rate rules. Add one to get started.</td></tr> :
                 rates.map(r => (
                  <tr key={r.id} className="border-b border-gray-100">
                    <td className="px-4 py-3 text-sm font-medium">{r.family || '(default)'}</td>
                    <td className="px-4 py-3 text-sm">{r.variant || '—'}</td>
                    <td className="px-4 py-3 text-sm">{r.state || 'All'}</td>
                    <td className="px-4 py-3 text-sm font-medium">₹{r.client_price}</td>
                    <td className="px-4 py-3 text-sm">₹{r.lawyer_payout}</td>
                    <td className="px-4 py-3 text-sm">₹{r.agent_payout}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.priority}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showRateForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="rate-form-modal">
              <form onSubmit={handleCreateRate} className="bg-white w-full max-w-lg border border-gray-200 rounded-sm">
                <div className="px-6 py-4 border-b border-gray-200"><h3 className="font-heading text-lg font-semibold">New Rate Rule</h3></div>
                <div className="p-6 grid grid-cols-2 gap-4">
                  {['family', 'variant', 'state'].map(f => (
                    <div key={f}>
                      <label className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-1.5 capitalize">{f}</label>
                      <input value={rateForm[f]} onChange={e => setRateForm({...rateForm, [f]: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:ring-1 focus:ring-black focus:outline-none" placeholder={f === 'family' ? 'passport, agreement...' : ''} />
                    </div>
                  ))}
                  {['client_price', 'lawyer_payout', 'agent_payout'].map(f => (
                    <div key={f}>
                      <label className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-1.5">{f.replace(/_/g, ' ')}</label>
                      <input type="number" step="0.01" required={f !== 'agent_payout'} value={rateForm[f]} onChange={e => setRateForm({...rateForm, [f]: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:ring-1 focus:ring-black focus:outline-none" placeholder="₹" />
                    </div>
                  ))}
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex gap-2 justify-end">
                  <button type="button" onClick={() => setShowRateForm(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="px-4 py-2 text-sm bg-black text-white rounded-sm hover:bg-gray-800">Create Rule</button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {selectedPayment && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" data-testid="payment-detail-drawer"
             onClick={() => setSelectedPayment(null)}>
          <aside className="w-[520px] max-w-full bg-white h-full overflow-y-auto border-l border-gray-200 p-6"
                 onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-semibold">Payment detail</h2>
              <button onClick={() => setSelectedPayment(null)} data-testid="payment-detail-close"
                      className="text-sm text-gray-500 hover:text-black">Close</button>
            </div>

            {detailLoading && <div className="text-sm text-gray-400">Loading...</div>}

            {!detailLoading && paymentDetail && (
              <>
                <div className="space-y-1.5 pb-4 mb-4 border-b border-gray-200">
                  <DetailRow k="Payment ID" v={paymentDetail.id} mono />
                  <DetailRow k="Order ID" v={paymentDetail.razorpay_order_id} mono />
                  <DetailRow k="Razorpay Payment ID" v={paymentDetail.razorpay_payment_id} mono />
                  <DetailRow k="Amount" v={`₹${paymentDetail.amount} ${paymentDetail.currency || ''}`} />
                  <DetailRow k="Status" v={
                    <span className={`inline-block px-2 py-0.5 text-[11px] font-mono uppercase rounded-sm ${STATUS_COLORS[paymentDetail.status] || 'bg-gray-100'}`}>
                      {paymentDetail.status}
                    </span>
                  } />
                  <DetailRow k="Method" v={paymentDetail.payment_method} />
                  <DetailRow k="Created" v={paymentDetail.created_at ? new Date(paymentDetail.created_at).toLocaleString() : '—'} />
                  {paymentDetail.captured_at && <DetailRow k="Captured" v={new Date(paymentDetail.captured_at).toLocaleString()} />}
                  {paymentDetail.failed_at && <DetailRow k="Failed" v={new Date(paymentDetail.failed_at).toLocaleString()} />}
                  {paymentDetail.failure_reason && (
                    <div className="mt-2 p-2 border border-red-200 bg-red-50 rounded-sm text-xs text-red-700">
                      {paymentDetail.failure_reason}
                    </div>
                  )}
                </div>

                {paymentDetail.case && (
                  <div className="pb-4 mb-4 border-b border-gray-200">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Linked case</div>
                    <div className="text-xs space-y-1">
                      <DetailRow k="Case ID" v={paymentDetail.case.id} mono />
                      {paymentDetail.case.case_number && <DetailRow k="Number" v={paymentDetail.case.case_number} mono />}
                      <DetailRow k="Service" v={paymentDetail.case.service_type} />
                      <DetailRow k="Status" v={paymentDetail.case.status} />
                      <DetailRow k="Payment status" v={paymentDetail.case.payment_status} />
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Webhook event timeline</div>
                    <span className="font-mono text-[10px] text-gray-400">{paymentEvents.length} events</span>
                  </div>
                  {paymentEvents.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No webhook events recorded yet.</p>
                  ) : (
                    <ul className="space-y-2" data-testid="payment-events-list">
                      {paymentEvents.map(ev => (
                        <li key={ev.id} className="text-xs border border-gray-200 rounded-sm p-2.5">
                          <div className="flex items-center gap-2 mb-1">
                            {ev.status === 'failed' || ev.error ? (
                              <XCircle className="w-3.5 h-3.5 text-red-500" />
                            ) : (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                            )}
                            <span className="font-mono font-medium">{ev.event || 'unknown'}</span>
                            <span className="ml-auto font-mono text-[10px] text-gray-400">
                              {ev.created_at ? new Date(ev.created_at).toLocaleString() : '—'}
                            </span>
                          </div>
                          <div className="font-mono text-[10px] text-gray-500 space-y-0.5">
                            {ev.razorpay_event_id && <div>event_id · {ev.razorpay_event_id}</div>}
                            {ev.payment_id && <div>payment · {ev.payment_id}</div>}
                            {ev.method && <div>method · {ev.method}</div>}
                            {ev.amount && <div>amount · ₹{(ev.amount / 100).toFixed(2)}</div>}
                            {ev.error && <div className="text-red-600">error · {ev.error}</div>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {(paymentDetail.metadata_events || []).length > 0 && (
                  <div className="mt-5">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                      Inline state transitions
                    </div>
                    <ul className="space-y-1">
                      {paymentDetail.metadata_events.map((m, i) => (
                        <li key={i} className="text-[11px] font-mono text-gray-600">
                          {new Date(m.at).toLocaleString()} — <span className="text-gray-900">{m.event}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

function DetailRow({ k, v, mono }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 py-1">
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 pt-0.5">{k}</div>
      <div className={`text-xs ${mono ? 'font-mono text-gray-600' : 'text-gray-800'} break-all`}>{v || '—'}</div>
    </div>
  );
}
